import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import apiService from '../../services/api';
import {
  normalizeSubscriptionStatus,
  getSubscriptionStatusLabelAr,
  type SubscriptionStatusCode,
} from '../../utils/subscriptionStatus';

interface Subscription {
  id: number;
  user_id: number;
  package_id: number;
  start_date: string;
  end_date: string;
  status: SubscriptionStatusCode | string;
  status_label?: string;
  amount_paid?: number;
  currency?: string;
  payment_method?: string;
  payment_status?: string;
  notes?: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  package?: {
    id: number;
    name: string;
    type: string;
    price: number;
    currency: string;
  };
}

interface Statistics {
  total_subscriptions: number;
  active_subscriptions: number;
  pending_subscriptions: number;
  expired_subscriptions: number;
  canceled_subscriptions: number;
  subscription_packages: number;
  advertisement_packages: number;
  revenue_this_month: number;
  revenue_this_year: number;
}

const SubscriptionsManagement: React.FC = observer(() => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [statistics, setStatistics] = useState<Statistics>({
    total_subscriptions: 0,
    active_subscriptions: 0,
    pending_subscriptions: 0,
    expired_subscriptions: 0,
    canceled_subscriptions: 0,
    subscription_packages: 0,
    advertisement_packages: 0,
    revenue_this_month: 0,
    revenue_this_year: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDisabling, setIsDisabling] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    package_type: '',
    search: ''
  });
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [subscriptionsRes, statsRes] = await Promise.all([
        apiService.getAllSubscriptions(filters),
        apiService.getSubscriptionStatistics()
      ]);
      
      const subscriptionsData = Array.isArray(subscriptionsRes.data) ? subscriptionsRes.data : [];
      setSubscriptions(subscriptionsData);
      setStatistics(statsRes.data);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = async (subscriptionId: number, newStatus: string) => {
    try {
      setError(null);
      const response = await apiService.updateSubscriptionStatus(subscriptionId.toString(), { status: newStatus });

      const statusCode = normalizeSubscriptionStatus(newStatus);
      setSubscriptions((prev) =>
        prev.map((s) =>
          s.id === subscriptionId
            ? {
                ...s,
                status: statusCode,
                status_label: getSubscriptionStatusLabelAr(statusCode),
              }
            : s
        )
      );

      if (!response.success) {
        await fetchData();
      }
    } catch (err: any) {
      setError(err.message);
      await fetchData();
    }
  };

  const handleDelete = async (subscriptionId: number) => {
    if (globalThis.confirm('هل أنت متأكد من حذف هذا الاشتراك؟')) {
      try {
        await apiService.deleteSubscription(subscriptionId.toString());
        fetchData();
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const handleViewDetails = async (subscriptionId: number) => {
    try {
      const response = await apiService.getSubscriptionDetails(subscriptionId.toString());
      console.log('Subscription details:', response.data);
      // يمكن إضافة modal لعرض التفاصيل لاحقاً
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDisableExpiredServices = async () => {
    if (!globalThis.confirm('هل أنت متأكد من تعطيل جميع الخدمات التي انتهت باقاتها؟')) {
      return;
    }

    try {
      setIsDisabling(true);
      setError(null);
      const response = await apiService.disableExpiredServices();
      
      if (response.success) {
        alert(response.message || 'تم تعطيل الخدمات المنتهية بنجاح');
        fetchData(); // Refresh data
      } else {
        setError(response.message || 'فشل في تعطيل الخدمات المنتهية');
      }
    } catch (err: any) {
      setError(err.message || 'حدث خطأ أثناء تعطيل الخدمات المنتهية');
    } finally {
      setIsDisabling(false);
    }
  };

  const getStatusBadge = (subscription: Subscription) => {
    const status = normalizeSubscriptionStatus(subscription.status);
    const statusClasses: Record<SubscriptionStatusCode, string> = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-red-100 text-red-800',
      canceled: 'bg-gray-100 text-gray-800',
    };

    const label = subscription.status_label || getSubscriptionStatusLabelAr(status);

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClasses[status]}`}>
        {label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'غير محدد';
    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return 'تاريخ غير صالح';
    }

    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  };

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">إدارة الاشتراكات</h1>
        <p className="text-gray-600">إدارة جميع الاشتراكات والاشتراكات في النظام</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">إجمالي الاشتراكات</p>
              <p className="text-2xl font-semibold text-gray-900">{statistics.total_subscriptions || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">اشتراكات نشطة</p>
              <p className="text-2xl font-semibold text-gray-900">{statistics.active_subscriptions || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">اشتراكات معلقة</p>
              <p className="text-2xl font-semibold text-gray-900">{statistics.pending_subscriptions || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">الإيرادات الشهرية</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(statistics.revenue_this_month || 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="subscription-status-filter" className="block text-sm font-medium text-gray-700 mb-2">حالة الاشتراك</label>
            <select
              id="subscription-status-filter"
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">جميع الحالات</option>
              <option value="active">نشط</option>
              <option value="pending">معلق</option>
              <option value="expired">منتهي</option>
              <option value="canceled">ملغي</option>
            </select>
          </div>

          <div>
            <label htmlFor="subscription-package-filter" className="block text-sm font-medium text-gray-700 mb-2">نوع الباقة</label>
            <select
              id="subscription-package-filter"
              value={filters.package_type}
              onChange={(e) => setFilters({...filters, package_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">جميع الأنواع</option>
              <option value="subscription">اشتراك</option>
              <option value="advertisement">إعلان</option>
            </select>
          </div>

          <div>
            <label htmlFor="subscription-search" className="block text-sm font-medium text-gray-700 mb-2">البحث</label>
            <input
              id="subscription-search"
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              placeholder="اسم المستخدم أو البريد الإلكتروني"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-indigo-50 via-white to-blue-50">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">قائمة الاشتراكات</h3>
              <p className="text-sm text-gray-500 mt-1">عدد الاشتراكات: {subscriptions.length}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleDisableExpiredServices}
                disabled={isDisabling}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-orange-600 text-white px-4 py-2.5 rounded-lg shadow-sm hover:from-red-700 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDisabling ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري المعالجة...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    تعطيل الخدمات المنتهية
                  </>
                )}
              </button>
              <button
                onClick={() => console.log('Create subscription modal - to be implemented')}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2.5 rounded-lg shadow-sm hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition"
              >
                إضافة اشتراك جديد
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-right">
            <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wide">المستخدم</th>
                <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wide">الباقة</th>
                <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wide">الحالة</th>
                <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wide">تاريخ البداية</th>
                <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wide">تاريخ النهاية</th>
                <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wide">المبلغ</th>
                <th className="px-6 py-3 text-xs font-semibold uppercase tracking-wide">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                        <svg className="w-7 h-7 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-lg font-semibold text-gray-700">لا توجد اشتراكات</p>
                      <p className="text-sm mt-1">لم يتم العثور على أي اشتراكات تطابق المعايير المحددة</p>
                    </div>
                  </td>
                </tr>
              ) : (
                subscriptions.map((subscription) => (
                  <tr key={subscription.id} className="transition-colors duration-150 odd:bg-white even:bg-gray-50 hover:bg-blue-50/60">
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-3">
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-900">
                            {subscription.user?.first_name} {subscription.user?.last_name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{subscription.user?.email}</div>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-blue-600 font-semibold">
                          {subscription.user?.first_name?.charAt(0) || subscription.user?.last_name?.charAt(0) || '?'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">{subscription.package?.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{subscription.package?.type}</div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      {getStatusBadge(subscription)}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatDate(subscription.start_date)}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatDate(subscription.end_date)}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {formatCurrency(subscription.amount_paid || subscription.package?.price || 0, subscription.currency || 'EUR')}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          onClick={() => handleViewDetails(subscription.id)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-full transition"
                        >
                          عرض
                        </button>
                        <select
                          key={`status-${subscription.id}-${normalizeSubscriptionStatus(subscription.status)}`}
                          value={normalizeSubscriptionStatus(subscription.status)}
                          onChange={(e) => handleStatusChange(subscription.id, e.target.value)}
                          className="text-xs font-semibold border border-gray-200 rounded-full px-3 py-1.5 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="active">نشط</option>
                          <option value="pending">معلق</option>
                          <option value="expired">منتهي</option>
                          <option value="canceled">ملغي</option>
                        </select>
                        <button
                          onClick={() => handleDelete(subscription.id)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-full transition"
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
    </div>
  );
});

export default SubscriptionsManagement;
